/* global analytics */
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import Media from 'react-media'
import queryString from 'qs'
import uniqBy from 'lodash/uniqBy'
import get from 'lodash/get'
import ahoy from 'ahoy.js'

import useIsServerSide from '../../hooks/useIsServerSideContext'
import useLoginModalState from '../../hooks/useLoginModalState'
import { useGetBucketListItemsIndexQuery } from '../../hooks/useQueries'
import { useUtmParams } from '../../utils/utmParams'

import { fetchEnquiryPrices } from '../../actions/enquiryActions'
import { openSelectEscape } from '../../components/SelectEscape/reduxSlice'
import { fetchStory, resetStory } from '../../actions/storyActions'
import { fetchListing, resetListing } from '../../actions/listingActions'
import { setActionToReplay } from '../../actions/authActions'
import { media, mediaMax } from '../../styles/media'

import { normalizeKeys } from '../../components/utils/propsUtils'
import Container from '../../components/layout/Container'
import Row from '../../components/layout/Row'
import ShareButton from '../../components/display/ShareButton'
import Button from '../../components/display/Button'
import ActivityMatchedIcon from '../../components/display/ActivityMatchedIcon'
import Avatar from '../../components/display/Avatar'
import HeartOnIcon from '../../components/icons/BucketlistHeartOn'
import HeartOffIcon from '../../components/icons/BucketlistHeartOff'
import InfoWithIcon from '../../components/display/InfoWithIcon'
import LazyImage from '../../components/display/LazyImage'
import Separator from '../../components/display/Separator'
import SpinnerContainer from '../../components/display/SpinnerContainer'
import Spinner from '../../components/display/Spinner'
import GalleryHeader from '../../components/display/GalleryHeader'
import ShowMoreText from '../../components/ShowMoreText'
import ShowMore from '../../components/ShowMore'
import StoriesIcon from '../../components/icons/Stories'
import QuickbookModal from '../../components/quickbook'
import MapWithListing from '../../components/maps/ListingMap'
import MobileActionBar from '../listing/Booking/MobileActionBar'
import TrackVisibility from '../../components/utils/TrackVisibility'
import ListingAlert from '../../components/display/ListingAlerts'
import ReviewsModal from '../../components/reviews/ReviewsModal'
import { Marker } from '../../components/maps/ListingMap/markers'
import { Legend, LegendItem } from '../listing/Sections/ListingLegend'
import DesktopListingHeader from '../listing/Header/DesktopListingHeader'
import {
  HeadingXxl,
  HeadingXl,
  HeadingLg,
} from '../../components/display/Typography'
import { BucketListButton } from '../../components/cards/shared/styled'
import {
  Section as BaseSection,
  SectionTitle,
  SectionSubtitle,
  SubSection,
  ListingWrapper,
} from '../listing/Sections/shared'
import ListingOverview from '../listing/Sections/ListingOverview'
import ListingDetailsList from '../listing/Sections/ListingDetailsList'
import ListingPricing from '../listing/Sections/ListingPricing'
import ListingAmenities from '../listing/Sections/ListingAmenities'
import Suggestions from '../../search/Suggestions'
import { featuredListingPromoImage } from '../../components/utils/storyUtils'
import {
  isExperience,
  isAccommodation,
  isPublished,
} from '../../components/utils/listingUtils'
import { makeItemKey } from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'

import AddToBucketList from './AddToBucketList'
import GiftThis from './GiftThis'
import StoryStepCard, {
  Wrapper as StoryStepCardClassName,
} from './StoryStepCard'
import StoryLiveMyAdventure from './Sections/StoryLiveMyAdventure'
import AboutStoriesBanner from './AboutStoriesBanner'
import DesktopBookingBar from './DesktopBookingBar'
import StoryMobileGallery from './StoryMobileGallery'

// Stupid safari bug that throws wrap off
const StepsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -32px;

  & > div {
    margin-top: -1px;
  }

  ${media.md`
    margin: 0 -15px;
    margin-bottom: -30px;
    & > div {
      margin-top: 0;
      margin-right: -1px;
      padding-bottom: 30px;
    }
  `}

  ${mediaMax.md`
    margin-bottom: -40px;

    ${StoryStepCardClassName} {
      border: none;
    }
  `}
`

const Section = styled(BaseSection)`
  ${mediaMax.md`
    padding: 16px 0;
  `}
`

const ActivityList = styled.ul`
  margin: 0;
  margin-bottom: 20px;
  padding: 0;
  list-style: none;

  display: flex;
  flex-wrap: wrap;

  li {
    padding: 0 0 20px;
    width: 50%;
  }

  @media (min-width: 768px) {
    li {
      width: 100%;
    }
  }
`

const Sponsor = styled.div`
  display: inline-block;
  background-color: ${({ theme }) => theme.brand.sand};
  padding: 20px;
  margin-bottom: 40px;

  a {
    color: ${({ theme }) => theme.brand.jungle};
  }
`

const MapWrapper = styled.div`
  height: 460px;

  ${media.md`
    width: 100%;
    margin: 0;
  `}
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;

  margin: -8px;
  margin-top: 30px;

  ${Button} {
    margin: 8px;

    .icon {
      font-size: 18px;
      margin-right: 10px;
    }
  }

  ${media.sm`
    margin: 0 -8px;
    flex-direction: row;

    ${Button} {
      margin: 0 8px;
    }
  `}
`

const MobileHeaderWrapper = styled.div`
  position: relative;

  ${media.md`
    display: none;
  `}
`

const FullWidthMobileContainer = styled(Container)`
  overflow: 'hidden';

  ${mediaMax.md`
    padding: 0;

    .container-fluid {
      padding: 0;
    }

    section {
      padding: 40px 16px;
    }
  `}
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -8px;

  ${Button} {
    margin: 0 8px;

    &:first-child {
      margin-bottom: 16px;
    }
  }

  ${media.md`
    flex-direction: row;
    max-width: 544px;

    ${Button} {
      flex: 1;
      &:first-child {
        margin-bottom: 0;
      }
    }
  `}
`

const Author = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${Avatar} {
    display: inline-block;
    width: 160px;
    height: 160px;
    margin: 0;
    margin-bottom: 20px;
  }

  ${mediaMax.lg`
    flex-direction: row;
    margin-bottom: 32px;
    text-align: left;
    ${Avatar} {
      width: 54px;
      height: 54px;
      margin: 0;
      margin-right: 16px;
    }
  `}
`

const StoryBy = styled.div`
  color: #999;
  font-size: 16px;
  line-height: 1.5;
  font-weight: normal;
  a {
    color: ${({ theme }) => theme.brand.jungle};
    text-decoration: none;
  }
`

const FeaturedListingImage = styled.div`
  position: relative;
  background-color: #333333;
  height: 0;
  width: 100%;
  padding-top: 66.666%;
  border-radius: 8px;
  margin: 24px 0;

  ${media.md`
    margin: 40px 0;
  `}

  ${LazyImage} {
    position: absolute;
    left: 0;
    top: 0;
  }
`

const FeaturedListingWrapper = styled.div`
  ${SectionTitle} {
    font-size: 20px;
    line-height: 2;
  }

  ${FeaturedListingImage} + ${Section} {
    padding: 0;
    border: none;
  }



  ${mediaMax.md`
    & > ${Section}:last-child {
      border-bottom: 1px solid #d1d1d1;
      padding-bottom: 40px !important;
    }
  `}
`

const DetailsWrapper = styled.div`
  ${SubSection} {
    margin-bottom: 0;
  }
`

const StoryLegend = styled(Legend)`
  flex-wrap: none;
  flex-direction: column;
`

const ListingPriceWrapper = styled.div`
  margin-top: 32px;
`

const SuggestionsWrapper = styled.section`
  margin: 40px 0;
`

const Story = ({
  dispatch,
  enquiry,
  storyFetched,
  storyFetching,
  filters,
  listing,
  listingFetching,
  location,
  match,
  story,
}) => {
  const [quickbookOpen, setQuickbookOpen] = useState(false)
  const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false)
  const [hideBookingBar, setHideBookingBar] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [pageViewTracked, setPageViewTracked] = useState(false)
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false)

  const theme = useTheme()

  const serverSide = useIsServerSide()
  const {
    currentUserSignedIn,
    currentUserLoading,
    openLoginModal,
  } = useLoginModalState()
  const utmParams = useUtmParams()

  const trackStoryViewed = () => {
    const { listing: featuredListing } = story.data.featured_listing

    analytics.track('Story Viewed', {
      story_id: story.data.id,
      story_title: story.data.title,
      has_video: !!story.data.video_url,
      listing_id: featuredListing.id,
      ...utmParams,
    })
    ahoy.track('Viewed story', {
      story_id: story.data.id,
    })
    analytics.track(
      'ViewContent',
      {
        // eslint-disable-line
        content_ids: [`story_${story.data.id}`],
        content_name: story.data.title,
        content_type: 'product',
        value: featuredListing.price,
        currency: featuredListing.currency,
        ...utmParams,
      },
      {
        integrations: {
          All: false,
          'Facebook Pixel': true,
          'Tiktok Conversions': true,
        },
      }
    )
  }

  const fetchPricing = (listingId, pricingFilters) => {
    const { adults, children, startDate, endDate } = pricingFilters
    if (startDate) {
      dispatch(
        fetchEnquiryPrices(listingId, {
          adults: adults || 2,
          children,
          start_date: startDate,
          end_date: endDate,
        })
      )
    }
  }

  const listingId = story?.data?.featured_listing?.listing?.id

  useEffect(() => {
    if (listingId) {
      dispatch(fetchListing(listingId))
    }
  }, [storyFetched, listingId])

  // Reset story and listing when component is unmounted.
  useEffect(() => {
    return () => {
      dispatch(resetStory())
      dispatch(resetListing())
    }
  }, [])

  useEffect(() => {
    if (storyFetched && listingId) {
      const { adults, children, startDate, endDate } = filters
      const pricingFilters = { adults, children, startDate, endDate }

      fetchPricing(listingId, pricingFilters)
    }
  }, [storyFetched, listingId])

  useEffect(() => {
    window.scrollTo(0, 0)

    if (
      !storyFetched ||
      (story.data.url !== location.pathname && !location.pathname.match(/\?/g))
    ) {
      dispatch(fetchStory(match.params.id))
    }

    if (!serverSide) {
      // always show the footer
      const $footer = document.querySelector('#site-footer')
      if ($footer) $footer.style.display = 'block'
    }
  }, [])

  useEffect(() => {
    if (story && story.data && pageViewTracked === false) {
      trackStoryViewed()
      setPageViewTracked(true)
    }
  }, [story])

  const {
    loading: bucketListItemsIndexLoading,
    data: bucketListItemsIndexData,
  } = useGetBucketListItemsIndexQuery()

  const bucketListItemsIndex =
    bucketListItemsIndexData?.currentUser?.bucketListItemsIndex
  const itemBucketListKey =
    story.data &&
    makeItemKey({
      itemId: story.data.id,
      itemType: 'Story',
    })

  const bucketListIds =
    bucketListItemsIndex &&
    itemBucketListKey &&
    (bucketListItemsIndex[itemBucketListKey] ?? [])
  const isInBucketList = bucketListIds?.length > 0

  const filterParams = () => {
    if (!filters) return ''
    const { startDate, endDate, adults, children } = filters

    return `${
      (startDate ? `&startDate=${startDate}` : '') +
      (endDate ? `&endDate=${endDate}` : '') +
      (adults ? `&adults=${adults}` : '') +
      (children ? `&children=${children}` : '')
    }`
  }

  const handleQuickbookOpen = () => {
    setMobileGalleryOpen(false)
    setQuickbookOpen(true)
  }

  const bucketListClickHandler = () => {
    const openSelectEscapeAction = openSelectEscape({
      itemId: story.data.id,
      itemType: 'Story',
      source: 'story_page',
    })

    if (!currentUserSignedIn) {
      openLoginModal()
      dispatch(setActionToReplay(openSelectEscapeAction))
    } else {
      dispatch(openSelectEscapeAction)
    }
  }

  const renderSponsor = () => {
    const { sponsor } = story.data

    if (!sponsor) {
      return null
    }

    const sponsorName = sponsor.url ? (
      <a href={sponsor.url} target="_blank" rel="noopener noreferrer">
        {sponsor.name}
      </a>
    ) : (
      sponsor.name
    )

    return <Sponsor>Thanks {sponsorName} for supporting this story.</Sponsor>
  }

  const renderActionButtons = () => {
    const featured = story.data.featured_listing

    return (
      <ActionButtons>
        <AddToBucketList
          onClick={bucketListClickHandler}
          fetching={bucketListItemsIndexLoading || currentUserLoading}
          isActive={isInBucketList}
        />
        {featured && featured.listing && featured.listing.giftable && (
          <GiftThis storyId={story.data.id} />
        )}
        <ShareButton
          title={`Check out ${story.data.title} on Riparide`}
          url={`https://riparide.com${story.data.url}`}
          source="story_page"
        />
      </ActionButtons>
    )
  }

  const renderFeaturedListing = () => {
    if (!listing || listingFetching) {
      return (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      )
    }

    return (
      <FeaturedListingWrapper className="featured-story-booking-card">
        <Section>
          {listing.listing_type.slug === 'accommodation' ? (
            <HeadingXl>Where We Stayed</HeadingXl>
          ) : (
            <HeadingXl>What We Booked</HeadingXl>
          )}

          <FeaturedListingImage>
            <LazyImage
              data-src={story.data.featured_listing_image.large}
              rounded
            />
          </FeaturedListingImage>

          <HeadingLg
            as="a"
            href={`${story.data.featured_listing.listing.url}?story_id=${story.data.id}`}
            style={{ color: theme?.['-ds-core-400']?.value }}
          >
            {listing.title}
          </HeadingLg>

          <div style={{ marginTop: '12px' }}>
            <ListingOverview
              listing={listing}
              noDescription
              openReviewModal={() => setReviewsModalOpen(true)}
            />
          </div>

          {listing.review_score > 0 && (
            <ReviewsModal
              reviewType="listing"
              reviewScore={listing.review_score}
              typeId={listing.id}
              isOpen={reviewsModalOpen}
              onRequestClose={() => {
                setReviewsModalOpen(false)
              }}
            />
          )}

          {!isExperience(listing) &&
            listing.amenities &&
            listing.amenities.length > 0 && (
              <Media query="(min-width: 992px)">
                {(isDesktop) => (
                  <ListingAmenities
                    listing={listing}
                    isDesktop={isDesktop}
                    style={{
                      ...(isDesktop ? {} : { padding: '16px 0' }),
                    }}
                  />
                )}
              </Media>
            )}
        </Section>

        <Section>
          <DetailsWrapper>
            <SectionTitle>Details</SectionTitle>
            <ListingDetailsList listing={listing} />

            <ShowMore>
              <ListingPriceWrapper>
                <SectionSubtitle>Prices</SectionSubtitle>
                <ListingPricing listing={listing} />
              </ListingPriceWrapper>
            </ShowMore>
          </DetailsWrapper>
        </Section>

        <Section style={{ paddingBottom: 0 }}>
          <SectionTitle>Learn More</SectionTitle>
          <SubSection>
            <p>
              For more information about {listing.title}, check out the listing
              or ask the host a question.
            </p>
          </SubSection>
          <ButtonWrapper>
            <Button
              as="a"
              href={`${listing.url}?story_id=${story.data.id}${filterParams()}`}
            >
              View listing
            </Button>
            <Button
              as="a"
              href={`/conversations?listing_id=${listing.id}`}
              ghost
              disabled={!isPublished(listing)}
            >
              Ask A Question
            </Button>
          </ButtonWrapper>
        </Section>
      </FeaturedListingWrapper>
    )
  }

  const renderStoryDetails = () => {
    const { user, featured_listing } = story.data

    return (
      <Row>
        <div className="col-xs-12 col-md-4">
          <Author>
            <Avatar>
              <LazyImage data-src={user.avatar} />
              <noscript>
                <LazyImage className="lazyload" src={user.avatar} />
              </noscript>
            </Avatar>
            <StoryBy>
              <span>Story by </span>
              <a href={user.url}>{user.first_name}</a>
            </StoryBy>
          </Author>
        </div>
        <div className="col-xs-12 col-md-8">
          <Section style={{ paddingTop: 0 }}>
            <div style={{ marginBottom: '20px' }}>
              <HeadingXxl style={{ marginBottom: '8px' }}>
                {story.data.title}
              </HeadingXxl>
              {story.data.featured_listing && (
                <InfoWithIcon>
                  <StoriesIcon /> Adventure in{' '}
                  <a
                    href={`/${featured_listing.listing.market.slug.toLowerCase()}/${
                      story.data.featured_listing.listing.region.slug
                    }`}
                  >
                    {featured_listing.listing.location.name}
                  </a>
                </InfoWithIcon>
              )}
            </div>

            {renderSponsor()}

            <ListingAlert
              listingId={featured_listing.listing?.id?.toString()}
              marketName={featured_listing.listing?.market?.name}
            />

            <ShowMoreText>{story.data.description}</ShowMoreText>
          </Section>
          {renderFeaturedListing()}
        </div>
      </Row>
    )
  }

  const renderAdventures = () => {
    const { adventures } = story.data

    return (
      <Row>
        <div className="col-xs-12 col-md-4">
          <HeadingXl>Things We Did</HeadingXl>
          <ActivityList>
            {uniqBy(
              adventures.map((a) => a.adventure_type),
              'id'
            ).map((adventure_type) => (
              <li key={adventure_type.id}>
                <ActivityMatchedIcon
                  iconName={adventure_type.slug}
                  iconSize="mid"
                />
                <span>{adventure_type.name}</span>
              </li>
            ))}
          </ActivityList>
        </div>
        <div className="col-xs-12 col-md-8">
          <StepsGrid className="row">
            {adventures.map((adventure) => (
              <div key={adventure.id} className="col-xs-12 col-sm-6">
                <StoryStepCard {...adventure} />
              </div>
            ))}
          </StepsGrid>
        </div>
      </Row>
    )
  }

  const renderMap = () => {
    const { listing: featuredListing } = story.data.featured_listing
    const { adventures } = story.data

    const slug = featuredListing.listing_subcategory
      ? featuredListing.listing_subcategory.slug
      : featuredListing.listing_type.slug

    const mapListing = featuredListing && {
      id: featuredListing.id,
      location: [featuredListing.location.lat, featuredListing.location.lng],
      type: 'Listing',
      slug,
      listing: featuredListing,
    }

    const mapAdventures =
      adventures?.map((adventure) => ({
        id: adventure?.id,
        type: 'Adventure',
        slug: adventure?.adventure_type?.slug,
        location: [adventure.location.lat, adventure.location.lng],
        notBucketListable: adventure.location_type === 'on_property',
        adventure: {
          ...normalizeKeys(adventure),
          storyteller: adventure?.storyteller && {
            ...normalizeKeys(adventure.storyteller),
            avatar: {
              thumbLg: adventure.storyteller.avatar_url,
            },
          },
        },
      })) || []

    return (
      <Row>
        <div className="col-xs-12 col-md-4">
          <HeadingXl>Our Adventure Map</HeadingXl>

          <StoryLegend>
            <LegendItem>
              <Marker jungle standalone>
                <i className={`icon icon-${slug}`} />
              </Marker>
              <span>
                {isAccommodation(featuredListing)
                  ? 'Where we stayed'
                  : 'What we booked'}
              </span>
            </LegendItem>
            <LegendItem>
              <Marker standalone>
                <i className="icon icon-experiences" />
              </Marker>
              <span>Things we did</span>
            </LegendItem>
          </StoryLegend>
        </div>
        <div className="col-xs-12 col-md-8">
          <SubSection intro>
            <p>
              We&apos;ve plotted our adventure on the map below. Select the map
              icons to discover more details.
            </p>
          </SubSection>
          <TrackVisibility
            onChange={(isVisible) => {
              if (isVisible) setShowMap(true)
            }}
          >
            <MapWrapper>
              <MapWithListing
                listing={mapListing}
                adventures={mapAdventures}
                hide={!showMap}
                analyticsSource="story_page"
              />
            </MapWrapper>
          </TrackVisibility>
        </div>
      </Row>
    )
  }

  const renderShare = () => {
    return (
      <Row>
        <div className="col-xs-12 col-md-4">
          <HeadingXl>Share The Love</HeadingXl>
        </div>
        <div className="col-xs-12 col-md-8">
          <SubSection intro>
            <p>
              Add this story to your bucket list or gift it to someone you think
              deserves it!
            </p>
          </SubSection>
          {renderActionButtons()}
        </div>
      </Row>
    )
  }

  if (!storyFetched || storyFetching) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  const photos = [
    { image: story.data.promo_image },
    { image: story.data.featured_listing_image },
    ...story.data.photos,
  ]

  const videos =
    story.data?.videos?.map(({ id, thumbnail_url, stream_url }) => ({
      id: id?.toString(),
      thumbnailUrl: thumbnail_url,
      streamUrl: stream_url,
    })) || []

  return (
    <ListingWrapper>
      <DesktopListingHeader
        photos={photos}
        videos={videos}
        video_url={story.data.video_url}
      />
      <MobileHeaderWrapper>
        <GalleryHeader
          backgroundImage={photos[0].image}
          photos={photos}
          videos={videos}
          video_url={story.data.video_url}
          taller
          onOpen={() => setMobileGalleryOpen(true)}
        />
        <BucketListButton
          onClick={bucketListClickHandler}
          disabled={bucketListItemsIndexLoading || currentUserLoading}
          isActive={isInBucketList}
        >
          <HeartOnIcon />
          <HeartOffIcon />
        </BucketListButton>
      </MobileHeaderWrapper>

      <Container style={{ overflow: 'hidden' }}>
        {renderStoryDetails()}

        <Separator />

        {renderAdventures()}

        <Separator style={{ display: 'block' }} />

        {renderMap()}

        <Separator />

        {renderShare()}
      </Container>
      <TrackVisibility onChange={(isVisible) => setHideBookingBar(isVisible)}>
        <div>
          <FullWidthMobileContainer>
            <StoryLiveMyAdventure
              story={story}
              onBookingButtonClick={() => setQuickbookOpen(true)}
            />
          </FullWidthMobileContainer>

          <SuggestionsWrapper>
            <Container>
              <Suggestions
                listing={story.data.featured_listing.listing}
                customTitle={
                  <HeadingXl>
                    More{' '}
                    {listing?.state && listing?.region && (
                      <a
                        href={`/${listing.state.toLowerCase()}/${
                          listing.region.slug
                        }`}
                      >
                        {listing?.region_name}
                      </a>
                    )}{' '}
                    Escapes
                  </HeadingXl>
                }
              />
            </Container>
          </SuggestionsWrapper>

          <Container>
            <AboutStoriesBanner />
          </Container>
        </div>
      </TrackVisibility>

      {story.data.featured_listing && (
        <QuickbookModal
          listingId={story.data.featured_listing.listing.id}
          storyId={story.data.id}
          isOpen={quickbookOpen}
          promoImage={featuredListingPromoImage(story.data, 'large')}
          onRequestClose={() => setQuickbookOpen(false)}
        />
      )}
      <Media query="(max-width: 767px)">
        <div>
          <MobileActionBar
            listing={story.data.featured_listing.listing}
            onShowBookingForm={() => setQuickbookOpen(true)}
          />
          <StoryMobileGallery
            storyId={story.data.id}
            videoUrl={story.data.video_url}
            isOpen={mobileGalleryOpen}
            onBook={handleQuickbookOpen}
            onRequestClose={() => setMobileGalleryOpen(false)}
            total={get(enquiry.data, 'total')}
          />
        </div>
      </Media>
      <Media query="(min-width: 768px)">
        <DesktopBookingBar
          isInBucketList={isInBucketList}
          listing={story.data.featured_listing.listing}
          storyId={story.data.id}
          promoImage={story.data.featured_listing_image.medium}
          onBook={() => setQuickbookOpen(true)}
          onBucketListButtonClick={bucketListClickHandler}
          visible={!hideBookingBar}
        />
      </Media>
    </ListingWrapper>
  )
}

export default connect(({ auth, story, listing, enquiry }, { location }) => ({
  auth,
  story,
  enquiry,
  filters: queryString.parse(location.search, { ignoreQueryPrefix: true }),
  listingFetching: listing.fetching,
  listing: listing.data && {
    ...listing.data,
    features: listing.features,
    amenities: listing.amenities,
    details: listing.details,
    pricing: listing.pricing,
  },
  storyFetched: story.fetched,
  storyFetching: story.fetching,
}))(Story)
